import { useEffect } from 'react'

import { CircularProgress, QuizEndingView, QuizPlayer, ThemeProvider } from '@guidde/design-system/legacy'
import { Box, Stack, styled } from '@mui/material'

import { LmsQuizContext, QuickGuiddeType, QuizProgress } from 'app/types'
import { getIdentificationMode } from 'components/QuizIdentification'
import { QuizIdentificationCard } from 'components/QuizIdentification/QuizIdentificationCard'
import { useWindowView } from 'hooks'

import { QuizPlaybackPlayer, useQuizPlayback } from './use-quiz-playback'

const Overlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    zIndex: 15,
    backgroundColor: '#fff',
    margin: -1,
    border: `2px solid ${theme.palette.indigo[100]}`,
}))

type Props = {
    playbook: QuickGuiddeType
    player: QuizPlaybackPlayer | null
    allowAnonymousQuiz?: boolean
    lmsQuizContext?: LmsQuizContext
    onQuizProgressChange?: (_quizzes: Array<QuizProgress>) => void
}

export const QuizPlaybookOverlay = ({
    playbook,
    player,
    allowAnonymousQuiz = true,
    lmsQuizContext,
    onQuizProgressChange,
}: Props) => {
    const { mdDown: isMobileView } = useWindowView()
    const {
        view,
        submitAttempt,
        continueGuide,
        rewatch,
        retake,
        skip,
        reviewGuide,
        identify,
        continueWithoutIdentity,
        isSkipAllowed,
    } = useQuizPlayback({ playbook, player, allowAnonymousQuiz, lmsQuizContext, onQuizProgressChange })

    const isWaitingForLearner = view.type === 'identify' && Boolean(lmsQuizContext?.isLearnerIdentityLoading)
    const isVisible = view.type !== 'idle'
    const shouldLockScroll = isMobileView && isVisible

    // lock background scroll while the full-screen mobile quiz is up
    useEffect(() => {
        if (!shouldLockScroll) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [shouldLockScroll])

    if (!isVisible) return null

    const renderContent = () => {
        if (isWaitingForLearner)
            return (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                >
                    <CircularProgress
                        size={40}
                        label="Loading"
                    />
                </Stack>
            )

        if (view.type === 'identify')
            return (
                <QuizIdentificationCard
                    key={view.quiz.id}
                    mode={getIdentificationMode(view.quiz)}
                    initialIdentity={lmsQuizContext?.learnerIdentity}
                    isSkipAllowed={isSkipAllowed}
                    onIdentify={identify}
                    onContinueWithoutIdentity={continueWithoutIdentity}
                />
            )

        if (view.type === 'quiz')
            return (
                <QuizPlayer
                    key={view.quiz.id}
                    quiz={view.quiz}
                    onSubmitAttempt={submitAttempt}
                    onContinue={continueGuide}
                    onRewatch={rewatch}
                    onRetake={retake}
                    onSkip={skip}
                />
            )

        return <QuizEndingView onReviewGuide={reviewGuide} />
    }

    return (
        <ThemeProvider>
            <Overlay
                {...(view.type === 'identify' && {
                    role: 'dialog',
                    'aria-modal': true,
                    'aria-label': 'Before we continue',
                })}
                sx={
                    isMobileView
                        ? {
                              position: 'fixed',
                              inset: 'auto',
                              top: 55,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              margin: 0,
                              borderRadius: 0,
                              overflowY: 'auto',
                              zIndex: 1200,
                          }
                        : undefined
                }
            >
                {renderContent()}
            </Overlay>
        </ThemeProvider>
    )
}
